import type { LucideIcon } from 'lucide-react';
import { Check, ChevronRight } from 'lucide-react';
import { DropdownMenu as M } from 'radix-ui';
import type { ReactElement, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import s from './Menu.module.css';

/** Dropdown menu (Radix). Compose Menu → MenuItem / MenuCheckboxItem / MenuRadioItem / MenuSub. */
export function Menu({
  trigger,
  children,
  align = 'end',
  label,
  open,
  onOpenChange,
}: {
  trigger: ReactElement;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  label?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <M.Root open={open} onOpenChange={onOpenChange}>
      <M.Trigger asChild>{trigger}</M.Trigger>
      <M.Portal>
        <M.Content className={s.content} align={align} sideOffset={6} collisionPadding={8} aria-label={label}>
          {children}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

export function MenuItem({
  icon: Icon,
  children,
  onSelect,
  danger,
  disabled,
  hint,
}: {
  icon?: LucideIcon;
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  return (
    <M.Item className={cx(s.item, danger && s.danger)} onSelect={onSelect} disabled={disabled}>
      {Icon ? <Icon className={s.icon} aria-hidden /> : <span className={s.iconSpace} />}
      <span className={s.text}>{children}</span>
      {hint && <span className={s.hint}>{hint}</span>}
    </M.Item>
  );
}

export function MenuCheckboxItem({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <M.CheckboxItem className={s.item} checked={checked} onCheckedChange={(v) => onChange(v === true)} onSelect={(e) => e.preventDefault()}>
      <span className={s.iconSpace}>
        <M.ItemIndicator>
          <Check className={s.icon} aria-hidden />
        </M.ItemIndicator>
      </span>
      <span className={s.text}>{children}</span>
    </M.CheckboxItem>
  );
}

export function MenuRadioGroup({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: ReactNode }) {
  return (
    <M.RadioGroup value={value} onValueChange={onChange}>
      {children}
    </M.RadioGroup>
  );
}

export function MenuRadioItem({ value, children, icon: Icon }: { value: string; children: ReactNode; icon?: LucideIcon }) {
  return (
    <M.RadioItem className={s.item} value={value}>
      {Icon ? <Icon className={s.icon} aria-hidden /> : <span className={s.iconSpace} />}
      <span className={s.text}>{children}</span>
      <M.ItemIndicator className={s.indicator}>
        <Check className={s.icon} aria-hidden />
      </M.ItemIndicator>
    </M.RadioItem>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <M.Label className={s.label}>{children}</M.Label>;
}

export function MenuSeparator() {
  return <M.Separator className={s.separator} />;
}

export function MenuSub({ icon: Icon, label, children }: { icon?: LucideIcon; label: ReactNode; children: ReactNode }) {
  return (
    <M.Sub>
      <M.SubTrigger className={s.item}>
        {Icon ? <Icon className={s.icon} aria-hidden /> : <span className={s.iconSpace} />}
        <span className={s.text}>{label}</span>
        <ChevronRight className={s.icon} aria-hidden />
      </M.SubTrigger>
      <M.Portal>
        <M.SubContent className={s.content} sideOffset={4} collisionPadding={8}>
          {children}
        </M.SubContent>
      </M.Portal>
    </M.Sub>
  );
}
